import { Card } from '../../components/ui/card';
import React, { useEffect, useRef, useState } from 'react';
import * as PhosphorIcons from '@phosphor-icons/react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

export default function DashboardLectures({ packId }) {
  const navigate = useNavigate();
  const [lectures, setLectures] = useState([]);

  const [showLeftShadow, setShowLeftShadow] = useState(false);
  const [showRightShadow, setShowRightShadow] = useState(false);
  const scrollContainerRef = useRef(null);

  const handleScroll = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    const isScrollable = scrollWidth > clientWidth;

    if (!isScrollable) {
      setShowLeftShadow(false);
      setShowRightShadow(false);
      return;
    }

    setShowLeftShadow(scrollLeft > 0);
    setShowRightShadow(scrollLeft < scrollWidth - clientWidth - 1);
  };

  useEffect(() => {
    async function fetchLectures() {
      try {
        if (!packId) return;
        const res = await axios.get(`/api/packs/${packId}/lectures`);
        setLectures(res?.data?.data || []);
      } catch (e) {
        setLectures([]);
      }
    }
    fetchLectures();
  }, [packId]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    handleScroll();
    container.addEventListener('scroll', handleScroll);
    const resizeObserver = new ResizeObserver(handleScroll);
    resizeObserver.observe(container);

    return () => {
      container.removeEventListener('scroll', handleScroll);
      resizeObserver.disconnect();
    };
  }, [lectures]);

  if (!lectures?.length) return null;

  const handleLectureClick = (lecture) => {
    const tab = lecture?.title ? encodeURIComponent(lecture.title) : '';
    navigate(`/v3/${packId}/lectures${tab ? `?tab=${tab}` : ''}`);
  };

  return (
    <div className="border-t-1 border-b-1 md:border-none py-3 px-4 md:px-2">
      <div className="w-full">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-2xl font-bold text-[var(--q3-neutral-default)] ">Lectures</h2>
        </div>

        <div className="relative">
          {showLeftShadow ? (
            <div className="absolute left-0 top-0 bottom-0 w-8 z-3 pointer-events-none [background:linear-gradient(to_right,var(--q3-neutral-default_inverted),transparent)]" />
          ) : null}

          {showRightShadow ? (
            <div className="absolute right-0 top-0 bottom-0 w-8 z-3 pointer-events-none [background:linear-gradient(to_left,var(--q3-neutral-default_inverted),transparent)]" />
          ) : null}

          <div ref={scrollContainerRef} className="flex gap-8 py-1 px-1 overflow-x-auto ">
            {lectures.map((lecture, index) => (
              <div key={lecture?._id || index} className="flex-shrink-0 text-center group overflow-visible">
                <Card
                  className={`bg-[${lecture.fillColor}] overflow-visible border-none shadow-none flex items-center justify-center h-13 rounded-full mx-auto w-28 transition-transform duration-300 cursor-pointer hover:scale-105`}
                  onClick={() => handleLectureClick(lecture)}
                >
                  <div className={`text-[${lecture.iconColor}]`}>
                    {PhosphorIcons[lecture.icon]
                      ? React.createElement(PhosphorIcons[lecture.icon], {
                          size: 24,
                          color: lecture.iconColor,
                        })
                      : null}
                  </div>
                </Card>

                <p className="mt-2 text-[var(--q3-neutral-default)] text-md font-medium text-center w-28 mx-auto break-words">
                  {lecture?.title}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
